/* eslint-disable */

import { useState, useEffect } from "react"

const maxRetryCount = 300
const oneTrustConsentCookieName = "OptanonAlertBoxClosed" // this is the name of the cookie that is added by OneTrust when a user accepts/closes cookies
// OneTrust targeting-cookies category. Ninetailed personalization is gated
// on this category, so we only treat consent as granted when it's active.
const NINETAILED_CONSENT_CATEGORY_ID = "C0004"

// Reads the active OneTrust groups (updated by OneTrust before
// OnConsentChanged fires) and reports whether the targeting category the
// so accept/reject is interpreted consistently across the app.
const hasNinetailedConsent = () => {
  if (typeof window === "undefined") return false
  const rawGroups = String(window.OnetrustActiveGroups || "")
  const groups = rawGroups
    .split(",")
    .map(group => group.trim())
    .filter(Boolean)
  const normalized = NINETAILED_CONSENT_CATEGORY_ID.replace(/^C0*/, "")
  const candidates = [
    NINETAILED_CONSENT_CATEGORY_ID,
    normalized,
    `C${normalized.padStart(4, "0")}`,
  ]
  return candidates.some(candidate => groups.includes(candidate))
}

// Polls for window.ninetailed.consent (attached lazily by the Ninetailed
// Privacy Plugin) and invokes it once available with the user's actual
// choice. Without this, an early banner interaction fires before the plugin
// initializes and the __nt-consent__ localStorage entry is never written —
// but we must only write "accepted" when the user accepted, otherwise a
// reject also ends up persisting __nt-consent__ = "accepted".
const fireNinetailedConsent = (hasConsent, retryCount = 0) => {
  if (typeof window === "undefined") return

  if (typeof window.ninetailed?.consent === "function") {
    window.ninetailed.consent(hasConsent)
    return
  }

  if (retryCount <= maxRetryCount) {
    setTimeout(() => fireNinetailedConsent(hasConsent, retryCount + 1), 100)
  }
}

const useCheckOneTrust = cookiesApi => {
  const [hasAcceptedOneTrust, setHasAcceptedOneTrust] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return
    }

    let aborted = false
    let oneTrustTimer = null

    // OneTrust is injected via next/script (afterInteractive), so it may not
    // be on window when this hook mounts. Poll until it is, then register the
    // OnConsentChanged callback that fires when the user accepts cookies.
    const registerOneTrustHandlers = (retryCount = 0) => {
      if (aborted) return

      if (window.OneTrust?.OnConsentChanged) {
        window.OneTrust.OnConsentChanged(() => {
          document.querySelectorAll(".skip-to-content")?.[0]?.blur()
          // OnConsentChanged fires for accept, reject, and custom saves
          // alike, so inspect the active groups and only flip Ninetailed
          // into the consented state (which writes __nt-consent__ =
          // "accepted") when the targeting category was actually accepted.
          fireNinetailedConsent(hasNinetailedConsent())
        })
        return
      }

      if (retryCount >= maxRetryCount) {
        // OneTrust never loaded on this environment - don't block content.
        setHasAcceptedOneTrust(true)
        return
      }

      oneTrustTimer = setTimeout(
        () => registerOneTrustHandlers(retryCount + 1),
        100,
      )
    }

    const handleWindowLoad = () => {
      registerOneTrustHandlers()
    }

    if (document.readyState === "complete") {
      handleWindowLoad()
    } else {
      window.addEventListener("load", handleWindowLoad)
    }

    return () => {
      aborted = true
      if (oneTrustTimer) clearTimeout(oneTrustTimer)
      window.removeEventListener("load", handleWindowLoad)
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    let activeTimer = null
    let aborted = false

    const checkOneTrust = retryCount => {
      const hasAcceptedOneTrust = cookiesApi?.get(oneTrustConsentCookieName)
      if (hasAcceptedOneTrust) {
        setHasAcceptedOneTrust(true)
        // VidyardEmbed?.api?.GDPR?.consent(true) // this breaks in prod
      } else if (retryCount <= maxRetryCount && !aborted) {
        activeTimer = setTimeout(() => {
          checkOneTrust(retryCount + 1)
        }, 1000)
      }
    }
    checkOneTrust(0)

    return () => {
      aborted = true
      if (activeTimer) {
        clearTimeout(activeTimer)
      }
    }
  }, [cookiesApi])

  return { hasAcceptedOneTrust }
}

export default useCheckOneTrust
